/**
 * TeamGantt Converter Utility
 * Extracted from TeamGanttImporter.tsx
 * Main conversion logic for TeamGantt CSV data
 */

import { parseCSV } from './csvParser';
import { parseWBS, generateId } from './wbsParser';
import { parseResources, parsePredecessors } from './dataParser';
import { EmptyTeamGanttImportError, TeamGanttImportError } from './teamGanttImportError';
import { COLOR_MAP } from '../constants/colorMap';
import { log } from '@/shared/utils/logger';
import type { ProjectData, ProjectNode } from '@/shared/types/projectData';

export interface ImportStats {
  totalNodes: number;
  projects: number;
  groups: number;
  tasks: number;
  milestones: number;
  withDependencies: number;
}

export interface ConversionResult {
  projectData: ProjectData;
  stats: ImportStats;
}

export { EmptyTeamGanttImportError, TeamGanttImportError } from './teamGanttImportError';

type ImportedNode = ProjectNode & { wbs: string; wbsLevel: number; estimatedHours: number; actualHours: number; _predecessors?: string[] };

const WBS_PATTERN = /^\d+(?:\.\d+)*$/;

function normalizeWBSValue(value: string): string {
  return value.trim().split('.').map(part => part.trim()).join('.');
}

function normalizeNodeType(value: string | undefined): 'group' | 'task' | 'milestone' {
  const type = (value || '').trim().toLowerCase() || 'task';
  if (type === 'group' || type === 'project') return 'group';
  if (type === 'task' || type === 'milestone') return type;
  throw new TeamGanttImportError('CSV contains unsupported node type "{{value}}"; expected project, group, task, or milestone.', { value: String(value) });
}

function normalizePercentComplete(value: string | undefined): number {
  const input = (value || '').trim();
  if (!input) return 0;
  const match = input.match(/^([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*(%)?$/);
  if (!match) throw new TeamGanttImportError('CSV contains invalid Percent Complete value "{{value}}".', { value: String(value) });
  const parsed = Number(match[1]);
  return Math.min(100, Math.max(0, parsed));
}

function normalizeHours(value: string | undefined, fieldName: string, wbs: string): number {
  const input = (value || '').trim();
  if (!input) return 0;
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(input)) {
    throw new TeamGanttImportError('CSV contains invalid {{field}} value "{{value}}" for WBS # "{{wbs}}".', { field: fieldName, value: String(value), wbs });
  }
  const parsed = Number(input);
  if (!Number.isFinite(parsed)) {
    throw new TeamGanttImportError('CSV contains invalid {{field}} value "{{value}}" for WBS # "{{wbs}}".', { field: fieldName, value: String(value), wbs });
  }
  return parsed;
}

function validCalendarDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function normalizeTeamGanttDate(value: string | undefined, fieldName: string, wbs: string): string | null {
  const input = (value || '').trim();
  if (!input) return null;

  let year: number;
  let month: number;
  let day: number;
  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const teamGanttMatch = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (isoMatch) {
    [, year, month, day] = isoMatch.map(Number);
  } else if (teamGanttMatch) {
    month = Number(teamGanttMatch[1]);
    day = Number(teamGanttMatch[2]);
    year = Number(teamGanttMatch[3]);
  } else {
    throw new TeamGanttImportError('CSV contains invalid {{field}} "{{value}}" for WBS # "{{wbs}}"; expected M/D/YYYY or YYYY-MM-DD.', { field: fieldName, value: String(value), wbs });
  }
  if (!validCalendarDate(year, month, day)) {
    throw new TeamGanttImportError('CSV contains invalid {{field}} "{{value}}" for WBS # "{{wbs}}".', { field: fieldName, value: String(value), wbs });
  }
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

/**
 * Convert TeamGantt CSV content to project data structure
 * Handles hierarchy, dependencies, dates, resources, and all metadata
 */
export function convertTeamGanttData(csvContent: string): ConversionResult {
  const rows = parseCSV(csvContent).filter(row => row.some(value => value.trim() !== ''));
  if (rows.length === 0) {
    throw new EmptyTeamGanttImportError();
  }

  const headers = rows[0];
  const expectedHeaders = [
    'WBS #', 'Name / Title', 'Type', 'Start Date', 'End Date',
    'Estimated Hours', 'Actual Hours', 'Percent Complete', 'Resources',
    'Predecessors', 'Notes', 'Comments', 'Task Color'
  ];

  const headerMap: Record<string, number> = {};
  expectedHeaders.forEach(header => {
    const index = headers.findIndex(h => h.trim() === header);
    if (index !== -1) {
      headerMap[header] = index;
    }
  });
  for (const header of ['WBS #', 'Name / Title']) {
    if (headerMap[header] === undefined) throw new TeamGanttImportError('CSV is missing the required {{column}} column', { column: header });
  }

  const dataRows = rows.slice(1);
  if (dataRows.length === 0) throw new EmptyTeamGanttImportError();
  const wbsIndex = headerMap['WBS #'];
  if (wbsIndex === undefined) throw new TeamGanttImportError('CSV is missing the WBS # column');
  const nameIndex = headerMap['Name / Title'];
  const seenWbs = new Set<string>();
  const generatedIds = new Set<string>();
  for (const row of dataRows) {
    const wbs = normalizeWBSValue(row[wbsIndex] || '');
    if (!wbs) throw new TeamGanttImportError('CSV contains a data row without a WBS #.');
    if (!(row[nameIndex] || '').trim()) throw new TeamGanttImportError('CSV contains WBS # "{{wbs}}" without a Name / Title.', { wbs });
    if (seenWbs.has(wbs)) throw new TeamGanttImportError('CSV contains duplicate WBS # "{{wbs}}"; resolve duplicate hierarchy identifiers before importing.', { wbs });
    seenWbs.add(wbs);
    const generatedId = generateId(wbs);
    if (generatedIds.has(generatedId)) throw new TeamGanttImportError('CSV WBS # "{{wbs}}" collides with another generated identifier; use unambiguous numeric WBS values.', { wbs });
    generatedIds.add(generatedId);
    if (!WBS_PATTERN.test(wbs)) throw new TeamGanttImportError('CSV contains invalid WBS # "{{wbs}}"; expected a dot-separated numeric hierarchy.', { wbs });
  }
  for (const wbs of seenWbs) {
    const parts = wbs.split('.');
    if (parts.length > 1) {
      const parentWbs = parts.slice(0, -1).join('.');
      if (!seenWbs.has(parentWbs)) {
        throw new TeamGanttImportError('CSV is missing parent WBS # "{{parent}}" required by WBS # "{{wbs}}".', { parent: parentWbs, wbs });
      }
    }
  }

  // Sort rows by WBS to ensure parents come before children
  // This ensures hierarchical order regardless of CSV row order
  const sortedDataRows = [...dataRows].sort((a, b) => {
    const wbsA = normalizeWBSValue((a[headerMap['WBS #']] || '').toString());
    const wbsB = normalizeWBSValue((b[headerMap['WBS #']] || '').toString());

    // Handle empty WBS (put at end)
    if (!wbsA && !wbsB) return 0;
    if (!wbsA) return 1;
    if (!wbsB) return -1;

    // Split WBS into parts for numeric comparison
    const partsA = wbsA.split('.').map(p => parseInt(p) || 0);
    const partsB = wbsB.split('.').map(p => parseInt(p) || 0);

    // Compare each part numerically
    for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
      const partA = partsA[i];
      const partB = partsB[i];
      if (partA !== partB) {
        return partA - partB;
      }
    }
    // A parent must sort before a child even when the child's next segment is zero.
    if (partsA.length !== partsB.length) return partsA.length - partsB.length;
    return wbsA.localeCompare(wbsB);
  });

  log.debug('TeamGantt Import', 'Processing rows in WBS order:',
    sortedDataRows.map(row => ({
      wbs: row[headerMap['WBS #']] || '',
      name: row[headerMap['Name / Title']] || ''
    }))
  );

  const nodes: ImportedNode[] = [];

  for (let i = 0; i < sortedDataRows.length; i++) {
    const row = sortedDataRows[i];

    const wbsStr = normalizeWBSValue(row[headerMap['WBS #']] || '');
    const name = row[headerMap['Name / Title']] || '';
    const type = normalizeNodeType(row[headerMap['Type']]);
    const startDate = normalizeTeamGanttDate(row[headerMap['Start Date']], 'Start Date', wbsStr);
    const endDate = normalizeTeamGanttDate(row[headerMap['End Date']], 'End Date', wbsStr);
    const estimatedHours = normalizeHours(row[headerMap['Estimated Hours']], 'Estimated Hours', wbsStr);
    const actualHours = normalizeHours(row[headerMap['Actual Hours']], 'Actual Hours', wbsStr);
    const percentComplete = normalizePercentComplete(row[headerMap['Percent Complete']]);
    const resources = parseResources(row[headerMap['Resources']]);
    const predecessors = parsePredecessors(row[headerMap['Predecessors']]);
    const notes = row[headerMap['Notes']] || '';
    const comments = row[headerMap['Comments']] || '';
    const taskColor = row[headerMap['Task Color']] || '';

    if (startDate && endDate && startDate > endDate) {
      throw new TeamGanttImportError('CSV contains an inverted date range for WBS # "{{wbs}}": Start Date must not be after End Date.', { wbs: wbsStr });
    }

    const wbs = parseWBS(wbsStr);
    const nodeId = generateId(wbsStr || `row_${i}`);
    log.debug('TeamGantt Import', `Generating ID for WBS "${wbsStr}": ${nodeId}`);

    // Determine parent based on WBS hierarchy
    let parentId: string | undefined;
    if (wbs.level > 0) {
      const parentWBS = wbsStr.split('.').slice(0, -1).join('.');
      // Parent existence was validated before conversion, so this reference is canonical.
      parentId = generateId(parentWBS);
      log.debug('TeamGantt Import', `Found parent for ${nodeId}: parent ID = ${parentId}`);
    }

    // Debug: Log parent assignment
    if (parentId) {
      log.debug('TeamGantt Import', `Node ${nodeId} (${name}) -> parentId: ${parentId}`);
    } else {
      log.debug('TeamGantt Import', `Node ${nodeId} (${name}) -> ROOT (no parent)`);
    }

    // Create base node
    const node: ImportedNode = {
      id: nodeId,
      type,
      title: name,
      parentId,
      order: i,
      wbs: wbsStr,
      wbsLevel: wbs.level,
      estimatedHours: estimatedHours,
      actualHours: actualHours,
      completionState: percentComplete >= 100 ? 'completed' : percentComplete > 0 ? 'in-progress' : 'not-started',
      priority: 'low',
      progress: Math.round(percentComplete),
      assignees: [],
      dependencies: [],
      linkedDocs: [],
      tags: [],
      attachments: [],
      attachmentCount: 0,
      hasImages: false,
      hasDocuments: false,
      viewState: {},
      version: 1,
      customFields: resources.length > 0 ? { teamGanttResources: resources } : {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Add type-specific fields
    if (type === 'milestone') {
      if (startDate) {
        node.date = startDate;
      }
    } else {
      if (startDate) node.startDate = startDate;
      if (endDate) node.endDate = endDate;

      // Progress and status
      if (percentComplete > 0) {
        node.progress = Math.round(percentComplete); // Convert to integer for backend compatibility
        if (percentComplete >= 100) {
          node.status = 'done';
        } else if (percentComplete > 0) {
          node.status = 'in-progress';
        } else {
          node.status = 'todo';
        }
      } else {
        node.status = 'todo';
      }
    }

    // TeamGantt exports display names, while project assignees require canonical member IDs.
    // Preserve source names as import metadata without creating invalid assignments.

    // Add description
    const description = [];
    if (notes.trim()) description.push(notes.trim());
    if (comments.trim()) description.push(`Comments: ${comments.trim()}`);
    if (description.length > 0) {
      node.description = description.join('\n\n');
    }

    // Add color
    if (taskColor && COLOR_MAP[taskColor]) {
      node.color = COLOR_MAP[taskColor];
    }

    // Add priority
    if (type === 'milestone') {
      node.priority = 'high';
    } else if (taskColor === 'red1') {
      node.priority = 'high';
    } else if (taskColor === 'yellow1') {
      node.priority = 'medium';
    } else {
      node.priority = 'low';
    }

    // Store predecessors for dependency processing
    if (predecessors.length > 0) {
      node._predecessors = predecessors;
    }

    nodes.push(node);
  }

  // Debug: Log hierarchy structure
  log.debug('TeamGantt Import', 'Node hierarchy:', nodes.map(n => ({
    id: n.id,
    wbs: n.wbs,
    title: n.title,
    parentId: n.parentId,
    hasParent: !!n.parentId
  })));

  // Process dependencies
  nodes.forEach(node => {
    if (node._predecessors) {
      const dependencies: string[] = [];
      node.dependencies = dependencies;
      node._predecessors.forEach((pred: string) => {
        const normalizedPred = normalizeWBSValue(pred);
        const predId = generateId(normalizedPred);
        const predNode = nodes.find(n => n.id === predId);
        if (predNode) {
          dependencies.push(predNode.id);
        } else {
          throw new TeamGanttImportError('CSV contains unresolved predecessor WBS # "{{predecessor}}" referenced by WBS # "{{wbs}}".', { predecessor: pred, wbs: node.wbs });
        }
      });
      delete node._predecessors;
    }
  });

  // Final validation - check parent_id values
  const nodesWithParents = nodes.filter(n => n.parentId).length;
  const rootNodes = nodes.filter(n => !n.parentId).length;
  log.debug('TeamGantt Import', `Final hierarchy check:
    - Total nodes: ${nodes.length}
    - Nodes WITH parentId: ${nodesWithParents}
    - Root nodes (no parentId): ${rootNodes}`);
  log.debug('TeamGantt Import', 'Nodes with parentId:', nodes.filter(n => n.parentId).map(n => ({
    id: n.id,
    title: n.title,
    parentId: n.parentId
  })));

  // Calculate stats
  const stats: ImportStats = {
    totalNodes: nodes.length,
    projects: 0,
    groups: nodes.filter(n => n.type === 'group').length,
    tasks: nodes.filter(n => n.type === 'task').length,
    milestones: nodes.filter(n => n.type === 'milestone').length,
    withDependencies: nodes.filter(n => n.dependencies && n.dependencies.length > 0).length
  };

  // Calculate root node IDs (nodes without parentId)
  const rootNodeIds = nodes.filter(node => !node.parentId).map(node => node.id);

  // Extract project name from the first root-level node or the first node with WBS "1"
  let projectName = "TeamGantt Import"; // Default fallback

  // Try to find the project name from:
  // 1. First node with WBS "1" (usually the project)
  // 2. First root-level node (no parent)
  // 3. First group/project type node
  const projectNode = nodes.find(n => n.wbs === "1") ||
    nodes.find(n => !n.parentId && n.type === 'group') ||
    nodes.find(n => !n.parentId);

  if (projectNode && projectNode.title) {
    projectName = projectNode.title;
    log.debug('TeamGantt Import', 'Extracted project name from CSV:', projectName);
  } else {
    log.debug('TeamGantt Import', 'Could not extract project name, using default:', projectName);
  }

  // Create project data structure
  const projectData: ProjectData = {
    version: "1.0",
    projectId: "teamgantt_import_" + Date.now(),
    projectName: projectName,
    nodes,
    rootNodeIds: rootNodeIds,
    kanbanColumns: [],
    files: [],
    fileAuditLogs: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metadata: {
      settings: {
        darkMode: false,
        autoSync: true,
        syncInterval: 30000,
        isCompactView: false,
        menuOrder: []
      }
    }
  };

  return { projectData, stats };
}
